import React from "react";
import { Field } from "formik";
import { Col, Form, Row } from "react-bootstrap";
import { RequiredFieldMarker } from "../../components/Forms/RequiredFieldMarker";
import { useViewport } from "../../contexts";
import type { ISiteOption, ICustomerMainLocation } from "../../customTypings/customer/Customer";
import { formatPostalAddressBlock, shouldShowFormFieldError } from "../../utils";

export interface CustomerEditFormGridProps {
  showSiteField: boolean;
  isNew: boolean;
  metaDeleted: boolean;
  siteId: string;
  setSiteId: (siteId: string) => void;
  siteDisplayName: string;
  sites: ISiteOption[];
  liveValidationAfterSubmit: boolean;
  errors: { name?: string };
  touched: { name?: boolean };
  mainLocation?: ICustomerMainLocation | null;
}

function GridFieldCell({ children }: { children: React.ReactNode }) {
  return <div className="mb-3">{children}</div>;
}

function CustomerSiteField({
  showSiteField,
  isNew,
  metaDeleted,
  siteId,
  setSiteId,
  siteDisplayName,
  sites,
}: Pick<
  CustomerEditFormGridProps,
  "showSiteField" | "isNew" | "metaDeleted" | "siteId" | "setSiteId" | "siteDisplayName" | "sites"
>) {
  if (!showSiteField) return null;

  return isNew ? (
    <Form.Group>
      <Form.Label>Site <RequiredFieldMarker /></Form.Label>
      <Form.Select
        value={siteId}
        onChange={(e) => setSiteId(e.target.value)}
        disabled={metaDeleted}
        aria-label="Site"
        required
      >
        <option value="">Select a site…</option>
        {sites.map((s) => (
          <option key={s.id} value={s.id}>
            {s.siteName}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  ) : (
    <Form.Group>
      <Form.Label>Site</Form.Label>
      <Form.Control readOnly disabled value={siteDisplayName} />
    </Form.Group>
  );
}

function CustomerNameField({
  metaDeleted,
  liveValidationAfterSubmit,
  errors,
  touched,
}: Pick<CustomerEditFormGridProps, "metaDeleted" | "liveValidationAfterSubmit" | "errors" | "touched">) {
  return (
    <Form.Group>
      <Form.Label>Name <RequiredFieldMarker /></Form.Label>
      <Field
        name="name"
        className={`form-control${shouldShowFormFieldError("name", errors, touched, liveValidationAfterSubmit) ? " is-invalid" : ""}`}
        disabled={metaDeleted}
      />
      {shouldShowFormFieldError("name", errors, touched, liveValidationAfterSubmit) && (
        <div className="invalid-feedback d-block">{errors.name}</div>
      )}
    </Form.Group>
  );
}

function CustomerEnabledCheckbox() {
  return (
    <div className="form-check">
      <Field id="Customer-is-enabled" name="isEnabled" type="checkbox" className="form-check-input" />
      <label className="form-check-label" htmlFor="Customer-is-enabled">
        Enabled
      </label>
    </div>
  );
}

function CustomerMainLocationField({ mainLocation }: { mainLocation: ICustomerMainLocation | null | undefined }) {
  const addressText = formatPostalAddressBlock(mainLocation, {
    locationName: mainLocation?.locationName,
  });

  return (
    <Form.Group>
      <Form.Label>Primary Address</Form.Label>
      {addressText ? (
        <div className="text-body" style={{ whiteSpace: "pre-line" }}>
          {addressText}
        </div>
      ) : (
        <div className="text-muted">No primary address assigned.</div>
      )}
    </Form.Group>
  );
}

export function CustomerEditFormGridLarge(props: CustomerEditFormGridProps) {
  return (
    <div className="vstack gap-2">
      <Row className="g-3">
        <Col lg={6}>
          {props.showSiteField && (
            <GridFieldCell>
              <CustomerSiteField {...props} />
            </GridFieldCell>
          )}
          <GridFieldCell>
            <CustomerNameField {...props} />
          </GridFieldCell>
          {props.isNew && (
            <GridFieldCell>
              <CustomerEnabledCheckbox />
            </GridFieldCell>
          )}
        </Col>
        {!props.isNew && (
          <Col lg={6}>
            <GridFieldCell>
              <CustomerMainLocationField mainLocation={props.mainLocation} />
            </GridFieldCell>
          </Col>
        )}
      </Row>
    </div>
  );
}

export function CustomerEditFormGridMedium(props: CustomerEditFormGridProps) {
  return <CustomerEditFormGridLarge {...props} />;
}

export function CustomerEditFormGridNarrow(props: CustomerEditFormGridProps) {
  return (
    <>
      {props.showSiteField && (
        <div className="mb-3">
          <CustomerSiteField {...props} />
        </div>
      )}
      <div className="mb-3">
        <CustomerNameField {...props} />
      </div>
      {!props.isNew && (
        <div className="mb-3">
          <CustomerMainLocationField mainLocation={props.mainLocation} />
        </div>
      )}
      {props.isNew && (
        <div className="mb-3">
          <CustomerEnabledCheckbox />
        </div>
      )}
    </>
  );
}

export function CustomerEditFormGrid(props: CustomerEditFormGridProps) {
  const { width } = useViewport();

  return width >= 768 ? (
    <CustomerEditFormGridLarge {...props} />
  ) : (
    <CustomerEditFormGridNarrow {...props} />
  );
}
